<script>
import Business from '@/services/Business';

export default {
  props: {
    selectedData: {
      type: Object,
      default: () => ({}),
    },
    userLimit: {
      type: [Number, String],
      default: null,
    },
    remainingSeats: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      peopleCount: 1,
      tryingToSubmit: false,
      errorMessage: '',
    };
  },
  computed: {
    registrantName() {
      const name = [this.selectedData?.name, this.selectedData?.surname]
        .filter(Boolean)
        .join(' ')
        .trim();
      return name || '—';
    },
    registrantEmail() {
      return this.selectedData?.email || '';
    },
    hasSelectedData() {
      return !!this.selectedData;
    },
    currentReservation() {
      const value = parseInt(this.selectedData?.reservation, 10);
      return isNaN(value) || value < 1 ? 1 : value;
    },
    parsedUserLimit() {
      const value = Number(this.userLimit);
      return isNaN(value) ? null : value;
    },
    parsedRemainingSeats() {
      const value = Number(this.remainingSeats);
      return isNaN(value) ? null : value;
    },
    maxPeopleCount() {
      const limits = [];
      if (this.parsedUserLimit !== null) {
        limits.push(this.parsedUserLimit);
      }
      if (this.parsedRemainingSeats !== null) {
        limits.push(this.parsedRemainingSeats + this.currentReservation);
      }
      if (!limits.length) {
        return null;
      }
      return Math.min(...limits);
    },
  },
  watch: {
    selectedData: {
      handler() {
        this.resetForm();
      },
      immediate: true,
    },
  },
  methods: {
    resetForm() {
      this.peopleCount = this.currentReservation;
      this.errorMessage = '';
      this.tryingToSubmit = false;
    },

    decrementPeopleCount() {
      if (this.peopleCount > 1) {
        this.peopleCount -= 1;
      }
    },

    incrementPeopleCount() {
      if (this.maxPeopleCount === null || this.peopleCount < this.maxPeopleCount) {
        this.peopleCount += 1;
      }
    },

    async saveChanges() {
      if (!this.selectedData?.event_id || !this.selectedData?.customer_id) {
        this.errorMessage = 'Registration data is incomplete.';
        return;
      }

      const reservation = parseInt(this.peopleCount, 10);
      if (isNaN(reservation) || reservation < 1) {
        this.errorMessage = 'Amount of people must be at least 1.';
        return;
      }
      if (this.maxPeopleCount !== null && reservation > this.maxPeopleCount) {
        this.errorMessage = `Maximum allowed: ${this.maxPeopleCount} ticket(s).`;
        return;
      }

      this.tryingToSubmit = true;
      this.errorMessage = '';

      try {
        const response = await Business.updateUserReservations(this.selectedData.event_id, {
          reservation,
          customer_id: this.selectedData.customer_id,
        });

        if (response.data?.success) {
          this.$emit('update-success', {
            ...this.selectedData,
            reservation,
          });
          this.$emit('onRefresh');
          this.$bvModal.hide('edit_user_reservations');
        } else {
          const err = response.data?.error;
          this.errorMessage = Array.isArray(err) ? err.join(', ') : err || 'Failed to save changes.';
        }
      } catch (error) {
        const err = error.response?.data?.error;
        this.errorMessage = Array.isArray(err) ? err.join(', ') : err || 'Failed to save changes.';
      } finally {
        this.tryingToSubmit = false;
      }
    },

    closeModal() {
      this.$bvModal.hide('edit_user_reservations');
    },
  },
};
</script>

<template>
  <b-modal
    id="edit_user_reservations"
    title="Edit registration"
    size="md"
    centered
    @shown="resetForm"
    @hidden="resetForm"
  >
    <p class="text-muted small mb-3">
      Update the ticket count for this registrant. This applies to the whole registration, not a single attendee row.
    </p>

    <div v-if="hasSelectedData" class="registrant-summary mb-4">
      <div><strong>Registrant:</strong> {{ registrantName }}</div>
      <div v-if="registrantEmail"><strong>Email:</strong> {{ registrantEmail }}</div>
      <div><strong>Current tickets:</strong> {{ currentReservation }}</div>
    </div>

    <div class="d-flex flex-column align-items-center mb-2">
      <label class="fw-semibold mb-2">Amount of people</label>
      <div class="d-flex align-items-center attendee-stepper">
        <b-button
          variant="outline-secondary"
          class="stepper-btn"
          :disabled="peopleCount <= 1"
          @click="decrementPeopleCount"
        >
          −
        </b-button>
        <span class="stepper-value mx-3">{{ peopleCount }}</span>
        <b-button
          variant="outline-secondary"
          class="stepper-btn"
          :disabled="maxPeopleCount !== null && peopleCount >= maxPeopleCount"
          @click="incrementPeopleCount"
        >
          +
        </b-button>
      </div>
      <small v-if="parsedUserLimit !== null" class="text-muted mt-1">
        There is a limit of {{ parsedUserLimit }} ticket(s) per person.
      </small>
      <small v-if="parsedRemainingSeats !== null" class="text-muted">
        Up to {{ maxPeopleCount }} ticket(s) available for this registration.
      </small>
    </div>

    <p v-if="errorMessage" class="text-danger mt-3 mb-0">{{ errorMessage }}</p>

    <template #modal-footer>
      <b-button variant="light" @click="closeModal">Cancel</b-button>
      <b-button variant="primary" :disabled="tryingToSubmit" @click="saveChanges">
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Save changes
      </b-button>
    </template>
  </b-modal>
</template>

<style scoped>
.registrant-summary {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fafafa;
}
.attendee-stepper .stepper-btn {
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  font-size: 1.25rem;
  line-height: 1;
  font-weight: 600;
}
.attendee-stepper .stepper-value {
  min-width: 2rem;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 600;
}
</style>
